import { useState } from 'react'
import dayjs from 'dayjs'

import type { HomeSummary } from '@/api/types'
import { useElementSize } from '@/ui'

type Day = HomeSummary['daily'][number]

const PLOT = 200
const LABELS = 28
const LEFT = 32

/** Eksenin üst sınırı: veriyi kapsayan 5'in en küçük katı (en az 5). */
function niceMax(v: number) {
  return Math.max(5, Math.ceil(v / 5) * 5)
}

/** Üstü ve altı ayrı yuvarlatılmış dikdörtgen. */
function bar(x: number, y: number, w: number, h: number, rt: number, rb: number) {
  if (h <= 0) return ''
  const t = Math.min(rt, w / 2, h / 2)
  const b = Math.min(rb, w / 2, h / 2)
  return `M${x},${y + t} Q${x},${y} ${x + t},${y} H${x + w - t} Q${x + w},${y} ${x + w},${y + t}`
    + ` V${y + h - b} Q${x + w},${y + h} ${x + w - b},${y + h} H${x + b} Q${x},${y + h} ${x},${y + h - b} Z`
}

/**
 * Günlük değişiklik çubukları: not (koyu) ve dosya (limon) üst üste.
 *
 * <p>Hafta sonları soluk — "neden düştü?" sorusunun cevabı çoğu zaman bu.
 * Bugün koyu ve kalın etiketli. Ayrıntı fareyle gelinen çubuğun ipucunda.
 */
export function ActivityChart({ days }: { days: Day[] }) {
  const { ref, width } = useElementSize<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)

  const W = Math.max(width, 200)
  const top = niceMax(Math.max(...days.map((d) => d.notes + d.files)))
  const scale = (v: number) => (v / top) * PLOT
  const gap = days.length > 14 ? 4 : days.length > 7 ? 8 : 14
  const slot = (W - LEFT) / days.length
  const bw = Math.max(3, slot - gap)
  const every = days.length > 14 ? 5 : days.length > 7 ? 2 : 1
  const grid = [top, Math.round((top * 2) / 3), Math.round(top / 3), 0]
  const total = days.reduce((a, d) => a + d.notes + d.files, 0)

  const h = hover != null ? days[hover] : null
  const hx = hover != null ? LEFT + slot * hover + slot / 2 : 0

  return (
    <div ref={ref} className="nx-activity" onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={W} height={PLOT + LABELS} viewBox={`0 0 ${W} ${PLOT + LABELS}`}
          className={`nx-chart${hover != null ? ' is-hovering' : ''}`} role="img"
          aria-label={`Son ${days.length} günde ${total} değişiklik`}>
          {grid.map((g, i) => {
            const y = PLOT - scale(g)
            return (
              <g key={i}>
                <line x1={LEFT} x2={W} y1={y} y2={y} className="nx-chart-grid" />
                <text x={LEFT - 8} y={y + 4} textAnchor="end" className="nx-chart-axis">{g}</text>
              </g>
            )
          })}

          {days.map((d, i) => {
            const x = LEFT + slot * i + (slot - bw) / 2
            const nh = scale(d.notes)
            const fh = scale(d.files)
            const fromEnd = days.length - 1 - i
            const today = fromEnd === 0
            const weekend = [0, 6].includes(dayjs(d.date).day())
            const label = today ? 'Bugün' : fromEnd % every === 0 && fromEnd > (every === 1 ? 0 : 1) ? dayjs(d.date).format('D MMM') : ''
            const cls = ['nx-chart-col', weekend && 'is-weekend', today && 'is-today', hover === i && 'is-on'].filter(Boolean).join(' ')
            return (
              <g key={d.date} className={cls}>
                <path className="nx-chart-note" d={bar(x, PLOT - nh, bw, nh, fh > 0 ? 2 : 5, 5)} />
                <path className="nx-chart-file" d={bar(x, PLOT - nh - fh - (nh > 0 && fh > 0 ? 2 : 0), bw, fh, 5, nh > 0 ? 2 : 5)} />
                {label && (
                  <text x={x + bw / 2} y={PLOT + 18} textAnchor="middle" className={today ? 'nx-chart-label is-today' : 'nx-chart-label'}>
                    {label}
                  </text>
                )}
                {/* İsabet alanı çubuktan geniş: ince çubuğa fareyle nişan almak gerekmesin. */}
                <rect x={LEFT + slot * i} y={0} width={slot} height={PLOT} fill="transparent" onMouseEnter={() => setHover(i)} />
              </g>
            )
          })}
        </svg>
      )}

      {h && (
        <div className="nx-chart-tip" style={{ left: Math.max(80, Math.min(W - 80, hx)), top: PLOT - scale(h.notes + h.files) - 10 }}>
          <strong>{dayjs(h.date).format('D MMMM, dddd')}</strong>
          <span><span>Not</span><span className="nx-chart-tip__n">{h.notes}</span></span>
          <span><span>Dosya</span><span className="nx-chart-tip__n">{h.files}</span></span>
          <span className="nx-chart-tip__total"><span>Toplam</span><span className="nx-chart-tip__n">{h.notes + h.files}</span></span>
        </div>
      )}
    </div>
  )
}
